import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchDashboard } from '../redux/slices/kpiSlice';
import { companyService } from '../services/api';
import {
  Box, Typography, Grid, Card, CardContent, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TablePagination,
  TableSortLabel, Paper, LinearProgress, FormControl, InputLabel,
  Select, MenuItem, TextField, Chip, IconButton, Tooltip,
  CircularProgress, Drawer, Divider, InputAdornment, Avatar,
  Button,
} from '@mui/material';
import {
  Assessment, TrendingUp, People, Assignment, Search, Visibility,
  Close, Email, Business, Person, Work, EmojiEvents, Warning,
  Refresh, CheckCircle, RadioButtonUnchecked, Cancel, RemoveCircle,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import {
  PieChart, Pie, Cell, Legend, ResponsiveContainer, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  LineChart, Line, ReferenceLine,
} from 'recharts';

// ─── Constants ────────────────────────────────────────────────────────────────

const NAVBAR_HEIGHT = 64;

const ROLE_LABELS = {
  superAdmin: 'Super Admin',
  coach: 'Coach',
  companyAdmin: 'Company Admin',
  manager: 'Manager',
  employee: 'Employee',
};

const ROLE_COLORS = {
  superAdmin: '#d32f2f',
  coach: '#7b1fa2',
  companyAdmin: '#1565c0',
  manager: '#e65100',
  employee: '#2e7d32',
};

const STATUS_CONFIG = {
  completed:   { label: 'Completed',    color: '#4caf50', bgcolor: '#4caf5018', textColor: '#2e7d32' },
  on_track:    { label: 'On Track',     color: '#2196f3', bgcolor: '#2196f318', textColor: '#1565c0' },
  in_progress: { label: 'In Progress',  color: '#9c27b0', bgcolor: '#9c27b018', textColor: '#6a1b9a' },
  at_risk:     { label: 'At Risk',      color: '#ff9800', bgcolor: '#ff980018', textColor: '#e65100' },
  critical:    { label: 'Critical',     color: '#f44336', bgcolor: '#f4433618', textColor: '#b71c1c' },
  overdue:     { label: 'Overdue',      color: '#9e1414', bgcolor: '#9e141418', textColor: '#7f0000' },
  no_kpi:      { label: 'No KPIs',      color: '#9e9e9e', bgcolor: '#9e9e9e18', textColor: '#616161' },
};

const STATUS_DISTRIBUTION = [
  { key: 'no_kpi',      label: 'No KPIs',          color: '#9e9e9e' },
  { key: 'overdue',     label: 'Overdue',           color: '#9e1414' },
  { key: 'critical',    label: 'Critical',          color: '#f44336' },
  { key: 'at_risk',     label: 'At Risk',           color: '#ff9800' },
  { key: 'in_progress', label: 'In Progress',       color: '#9c27b0' },
  { key: 'on_track',    label: 'On Track',          color: '#2196f3' },
  { key: 'completed',   label: 'Completed',         color: '#4caf50' },
];

const TASK_STATUS_COLORS = {
  achieved:     '#4caf50',
  inProgress:   '#2196f3',
  notAchieved:  '#f44336',
  notStarted:   '#bdbdbd',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Client-side status fallback — used when the backend hasn't returned a `status`
// field yet (e.g. before server restart picks up the new code).
const getUserStatus = (u) => {
  if (u.status) return u.status;
  if (!u.totalTasks || u.totalTasks === 0) return 'no_kpi';
  if (u.completionPercentage >= 100) return 'completed';
  if (u.completionPercentage >= 70) return 'on_track';
  if (u.completionPercentage >= 40) return 'at_risk';
  return 'critical';
};

const getProgressColor = (pct) => {
  if (pct >= 75) return '#4caf50';
  if (pct >= 50) return '#ffc107';
  if (pct >= 25) return '#ff9800';
  return '#f44336';
};

const getInitials = (name = '') =>
  name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();

const fmtPct = (v) => `${Math.round(v)}%`;

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatCard = ({ title, value, subtitle, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ pb: '16px !important' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{
          bgcolor: `${color}18`,
          borderRadius: 2,
          p: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          {React.cloneElement(icon, { sx: { fontSize: 28, color } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const DistributionChart = ({ buckets, total }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    {buckets.map((b) => (
      <Box key={b.key}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: b.color, flexShrink: 0 }} />
            <Typography variant="body2">{b.label}</Typography>
          </Box>
          <Typography variant="body2" fontWeight="medium">
            {b.count}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              ({total ? Math.round((b.count / total) * 100) : 0}%)
            </Typography>
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={total ? (b.count / total) * 100 : 0}
          sx={{
            height: 8, borderRadius: 4,
            bgcolor: `${b.color}22`,
            '& .MuiLinearProgress-bar': { bgcolor: b.color, borderRadius: 4 },
          }}
        />
      </Box>
    ))}
  </Box>
);

const PerformerRow = ({ rank, user, variant = 'top' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
    <Typography variant="caption" color="text.secondary" sx={{ width: 16, textAlign: 'center', fontWeight: 'bold' }}>
      {rank}
    </Typography>
    <Avatar sx={{ width: 32, height: 32, fontSize: 12, bgcolor: variant === 'top' ? '#4caf5022' : '#f4433622', color: variant === 'top' ? '#4caf50' : '#f44336' }}>
      {getInitials(user.name)}
    </Avatar>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" noWrap fontWeight="medium">{user.name}</Typography>
      <LinearProgress
        variant="determinate"
        value={user.completionPercentage}
        sx={{
          height: 4, borderRadius: 2, mt: 0.25,
          bgcolor: '#e0e0e0',
          '& .MuiLinearProgress-bar': { bgcolor: getProgressColor(user.completionPercentage) },
        }}
      />
    </Box>
    <Typography variant="body2" fontWeight="bold" sx={{ color: getProgressColor(user.completionPercentage), minWidth: 36, textAlign: 'right' }}>
      {fmtPct(user.completionPercentage)}
    </Typography>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const Dashboard = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { dashboard, dashboardLoading: loading, error } = useSelector((state) => state.kpi);
  const { stats, users: dashboardUsers, trend: trendData } = dashboard;

  const isMultiCompany = ['superAdmin', 'coach'].includes(user?.role);

  // Top-level company context (coach / superAdmin only)
  const [companyFilter, setCompanyFilter] = useState('all');
  const [selectorCompanies, setSelectorCompanies] = useState([]);

  // Filters
  const [filters, setFilters] = useState({ search: '', status: 'all', dateFrom: null, dateTo: null });

  // Table
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ field: 'completionPercentage', direction: 'desc' });

  // Drawer
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => { dispatch(fetchDashboard()); }, [dispatch]);

  useEffect(() => {
    if (isMultiCompany) {
      companyService.getCompanies()
        .then(res => setSelectorCompanies(res.data || []))
        .catch(() => {});
    }
  }, [isMultiCompany]);

  // Fetch fresh dashboard data whenever company selection changes
  useEffect(() => {
    dispatch(fetchDashboard(companyFilter));
    setFilters({ search: '', status: 'all', dateFrom: null, dateTo: null });
    setPage(0);
  }, [companyFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const aggregates = useMemo(() => {
    const totalTasks     = dashboardUsers.reduce((s, u) => s + (u.totalTasks || 0), 0);
    const completedTasks = dashboardUsers.reduce((s, u) => s + (u.completedTasks || 0), 0);
    const noKPIs         = dashboardUsers.filter(u => u.totalTasks === 0).length;
    return { totalTasks, completedTasks, noKPIs, remaining: totalTasks - completedTasks };
  }, [dashboardUsers]);

  // Table display users: dashboardUsers filtered by search/status/date controls
  const displayUsers = useMemo(() => {
    let result = dashboardUsers;
    if (filters.search) result = result.filter(u => u.name.toLowerCase().includes(filters.search.toLowerCase()));
    if (filters.status !== 'all') result = result.filter(u => getUserStatus(u) === filters.status);
    if (filters.dateFrom && filters.dateTo) result = result.filter(u => {
      const d = new Date(u.lastUpdate);
      return d >= filters.dateFrom && d <= filters.dateTo;
    });
    return result;
  }, [dashboardUsers, filters]);

  const statusDistribution = useMemo(() => {
    const counts = {};
    STATUS_DISTRIBUTION.forEach(b => { counts[b.key] = 0; });
    displayUsers.forEach(u => {
      const s = getUserStatus(u);
      if (counts[s] !== undefined) counts[s]++;
    });
    return STATUS_DISTRIBUTION.map(b => ({ ...b, count: counts[b.key] }));
  }, [displayUsers]);

  const topPerformers = useMemo(() =>
    [...dashboardUsers]
      .filter(u => u.totalTasks > 0)
      .sort((a, b) => b.completionPercentage - a.completionPercentage)
      .slice(0, 5),
    [dashboardUsers]
  );

  const bottomPerformers = useMemo(() =>
    [...dashboardUsers]
      .filter(u => u.totalTasks > 0)
      .sort((a, b) => a.completionPercentage - b.completionPercentage)
      .slice(0, 5),
    [dashboardUsers]
  );

  const taskStatusData = useMemo(() => {
    const totals = { achieved: 0, inProgress: 0, notAchieved: 0, notStarted: 0 };
    dashboardUsers.forEach(u => {
      totals.achieved    += u.achieved    || 0;
      totals.inProgress  += u.inProgress  || 0;
      totals.notAchieved += u.notAchieved || 0;
      totals.notStarted  += u.notStarted  || 0;
    });
    return [
      { name: 'Achieved',     value: totals.achieved,    color: TASK_STATUS_COLORS.achieved },
      { name: 'In Progress',  value: totals.inProgress,  color: TASK_STATUS_COLORS.inProgress },
      { name: 'Not Achieved', value: totals.notAchieved, color: TASK_STATUS_COLORS.notAchieved },
      { name: 'Not Started',  value: totals.notStarted,  color: TASK_STATUS_COLORS.notStarted },
    ].filter(d => d.value > 0);
  }, [dashboardUsers]);

  const companyPerformance = useMemo(() => {
    if (!isMultiCompany) return [];
    if (companyFilter !== 'all') {
      // Single company selected — show per-user bars
      return [...dashboardUsers]
        .sort((a, b) => b.completionPercentage - a.completionPercentage)
        .map(u => ({ name: u.name, value: Math.round(u.completionPercentage) }));
    }
    // All companies — aggregate by company
    const map = {};
    dashboardUsers.forEach(u => {
      if (!u.company) return;
      const key = u.company.id || u.company._id;
      if (!map[key]) map[key] = { name: u.company.name, total: 0, count: 0 };
      map[key].total += u.completionPercentage;
      map[key].count++;
    });
    return Object.values(map)
      .map(c => ({ name: c.name, value: Math.round(c.total / c.count) }))
      .sort((a, b) => b.value - a.value);
  }, [dashboardUsers, isMultiCompany, companyFilter]);

  const sortedFilteredUsers = useMemo(() => {
    return [...displayUsers].sort((a, b) => {
      let av = a[sortConfig.field], bv = b[sortConfig.field];
      if (sortConfig.field === 'lastUpdate') { av = new Date(av); bv = new Date(bv); }
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [displayUsers, sortConfig]);

  const paginatedUsers = useMemo(() =>
    sortedFilteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedFilteredUsers, page, rowsPerPage]
  );

  // ── Handlers ──

  const handleFilter = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // ── Early returns ──

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Box sx={{ p: 3 }}>
      <Typography color="error" gutterBottom>{error}</Typography>
      <Button variant="outlined" startIcon={<Refresh />} onClick={() => dispatch(fetchDashboard())}>
        Retry
      </Button>
    </Box>
  );

  // ── Render ──

  return (
    <Box sx={{ p: 3 }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Dashboard</Typography>
          {isMultiCompany && companyFilter !== 'all' && (
            <Typography variant="caption" color="text.secondary">
              {selectorCompanies.find(c => c._id === companyFilter)?.name}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isMultiCompany && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={companyFilter}
                onChange={e => setCompanyFilter(e.target.value)}
                displayEmpty
                sx={{ fontSize: 14 }}
              >
                <MenuItem value="all">All Companies</MenuItem>
                {selectorCompanies.map(c => (
                  <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Tooltip title="Refresh data">
            <IconButton onClick={() => dispatch(fetchDashboard())} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Row 1: Stat Cards ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            subtitle={aggregates.noKPIs > 0 ? `${aggregates.noKPIs} with no KPIs assigned` : 'All users have KPIs'}
            icon={<People />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Avg. Completion"
            value={fmtPct(stats.avgCompletion)}
            subtitle={`${aggregates.totalTasks.toLocaleString()} total tasks`}
            icon={<Assessment />}
            color="#4caf50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="On Track"
            value={stats.onTrack}
            subtitle={stats.totalUsers ? `${Math.round((stats.onTrack / stats.totalUsers) * 100)}% of team` : '—'}
            icon={<TrendingUp />}
            color="#4caf50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Need Attention"
            value={stats.needAttention}
            subtitle={`${aggregates.remaining.toLocaleString()} tasks remaining`}
            icon={<Assignment />}
            color="#f44336"
          />
        </Grid>
      </Grid>

      {/* ── Row 2: Distribution + Performers ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>

        {/* Completion Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Status Distribution
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Deadline-aware status breakdown across {displayUsers.length} user{displayUsers.length !== 1 ? 's' : ''}
              {displayUsers.length !== stats.totalUsers ? ` (filtered from ${stats.totalUsers})` : ''}
            </Typography>
            {displayUsers.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180 }}>
                <Typography color="text.secondary">No data available</Typography>
              </Box>
            ) : (
              <DistributionChart buckets={statusDistribution} total={displayUsers.length} />
            )}
          </Paper>
        </Grid>

        {/* Top + Bottom Performers */}
        <Grid item xs={12} md={6}>
          <Grid container spacing={2} sx={{ height: '100%' }}>

            {/* Top Performers */}
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <EmojiEvents sx={{ color: '#ffc107', fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight="bold">Top Performers</Typography>
                </Box>
                {topPerformers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No data yet</Typography>
                ) : (
                  topPerformers.map((u, i) => (
                    <PerformerRow key={u.id} rank={i + 1} user={u} variant="top" />
                  ))
                )}
              </Paper>
            </Grid>

            {/* Need Attention */}
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Warning sx={{ color: '#f44336', fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight="bold">Need Attention</Typography>
                </Box>
                {bottomPerformers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No data yet</Typography>
                ) : (
                  bottomPerformers.map((u, i) => (
                    <PerformerRow key={u.id} rank={i + 1} user={u} variant="bottom" />
                  ))
                )}
              </Paper>
            </Grid>

          </Grid>
        </Grid>
      </Grid>

      {/* ── Row 3: Trend + Task Status / Company Performance ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>

        {/* Progress Trend */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Progress Trend
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Average team completion over the last 6 months — dashed line marks the 70% on-track threshold
            </Typography>
            {trendData.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240 }}>
                <Typography color="text.secondary">No trend data available</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={trendData} margin={{ top: 8, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 12 }} width={40} />
                  <RechartsTooltip formatter={v => [`${v}%`, 'Avg Completion']} />
                  <ReferenceLine y={70} stroke="#4caf50" strokeDasharray="4 4" label={{ value: '70%', position: 'right', fontSize: 11, fill: '#4caf50' }} />
                  <Line type="monotone" dataKey="value" stroke="#1990bb" strokeWidth={2.5} dot={{ r: 4, fill: '#1990bb' }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Right panel: Company Performance (coach/superAdmin) OR Task Status Breakdown (others) */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.5, height: '100%' }}>

            {isMultiCompany ? (
              /* Company Performance bar chart */
              <>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {companyFilter === 'all' ? 'Company Performance' : 'User Performance'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {companyFilter === 'all'
                    ? 'Average KPI completion per company'
                    : `KPI completion for ${selectorCompanies.find(c => c._id === companyFilter)?.name ?? 'selected company'}`}
                </Typography>
                {companyPerformance.length === 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240 }}>
                    <Typography color="text.secondary">No data available</Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={Math.max(240, companyPerformance.length * 36)}>
                    <BarChart
                      data={companyPerformance}
                      layout="vertical"
                      margin={{ top: 0, right: 40, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                      <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 12 }} />
                      <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} />
                      <RechartsTooltip formatter={v => [`${v}%`, companyFilter === 'all' ? 'Avg Completion' : 'Completion']} />
                      <ReferenceLine x={70} stroke="#4caf50" strokeDasharray="4 4" />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {companyPerformance.map((entry, i) => (
                          <Cell key={i} fill={getProgressColor(entry.value)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </>
            ) : (
              /* Task Status Breakdown donut */
              <>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Task Status Breakdown
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  All {aggregates.totalTasks.toLocaleString()} tasks across your team
                </Typography>
                {taskStatusData.length === 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240 }}>
                    <Typography color="text.secondary">No task data available</Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={taskStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {taskStatusData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(v, name) => [v.toLocaleString(), name]} />
                      <Legend iconType="circle" iconSize={10} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </>
            )}

          </Paper>
        </Grid>
      </Grid>

      {/* ── Row 4: Filters + Users Table ── */}
      <Paper sx={{ mb: 3 }}>

        {/* Filters bar */}
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                placeholder="Search users…"
                value={filters.search}
                onChange={e => handleFilter('search', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={filters.status} label="Status" onChange={e => handleFilter('status', e.target.value)}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="on_track">On Track</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="at_risk">At Risk</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                  <MenuItem value="no_kpi">No KPIs</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="From" value={filters.dateFrom}
                  onChange={d => handleFilter('dateFrom', d)}
                  renderInput={params => (
                    <TextField {...params} fullWidth />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6} sm>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="To" value={filters.dateTo}
                  onChange={d => handleFilter('dateTo', d)}
                  renderInput={params => (
                    <TextField {...params} fullWidth />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            {(filters.search || filters.status !== 'all' || filters.dateFrom || filters.dateTo) && (
              <Grid item xs="auto">
                <Button size="small" onClick={() => {
                  setFilters({ search: '', status: 'all', dateFrom: null, dateTo: null });
                  setPage(0);
                }}>
                  Clear
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortConfig.field === 'name'}
                    direction={sortConfig.field === 'name' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('name')}
                  >User</TableSortLabel>
                </TableCell>
                {isMultiCompany && <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>}
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>
                  <TableSortLabel
                    active={sortConfig.field === 'completionPercentage'}
                    direction={sortConfig.field === 'completionPercentage' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('completionPercentage')}
                  >Progress</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortConfig.field === 'lastUpdate'}
                    direction={sortConfig.field === 'lastUpdate' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('lastUpdate')}
                  >Last Update</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isMultiCompany ? 7 : 6} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      {dashboardUsers.length === 0
                        ? 'No users found. Assign KPIs to team members to see their progress here.'
                        : 'No users match the current filters.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map(u => (
                  <TableRow key={u.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: '#1990bb22', color: '#1990bb' }}>
                          {getInitials(u.name)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">{u.name}</Typography>
                          {u.totalTasks === 0 && (
                            <Typography variant="caption" color="text.secondary">No KPIs</Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    {isMultiCompany && (
                      <TableCell>
                        <Typography variant="body2">{u.company?.name || '—'}</Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="caption" sx={{ color: ROLE_COLORS[u.role] || '#666', fontWeight: 500 }}>
                        {ROLE_LABELS[u.role] || u.role}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={u.completionPercentage}
                          sx={{
                            flex: 1, height: 6, borderRadius: 3,
                            bgcolor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': { bgcolor: getProgressColor(u.completionPercentage) },
                          }}
                        />
                        <Typography variant="caption" fontWeight="medium" sx={{ minWidth: 30 }}>
                          {fmtPct(u.completionPercentage)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const cfg = STATUS_CONFIG[getUserStatus(u)] || STATUS_CONFIG.no_kpi;
                        return (
                          <Chip
                            label={cfg.label}
                            size="small"
                            sx={{ fontSize: 11, bgcolor: cfg.bgcolor, color: cfg.textColor, fontWeight: 600 }}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {u.totalTasks === 0 ? '—' : new Date(u.lastUpdate).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View details">
                        <IconButton size="small" onClick={() => setSelectedUser(u)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={sortedFilteredUsers.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Rows:"
        />
      </Paper>

      {/* ── User Detail Drawer ── */}
      <Drawer
        anchor="right"
        open={Boolean(selectedUser)}
        onClose={() => setSelectedUser(null)}
        sx={{ '& .MuiDrawer-paper': { top: NAVBAR_HEIGHT, height: `calc(100% - ${NAVBAR_HEIGHT}px)`, width: 360 } }}
      >
        {selectedUser && (
          <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 44, height: 44, bgcolor: '#1990bb22', color: '#1990bb', fontWeight: 'bold' }}>
                  {getInitials(selectedUser.name)}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>{selectedUser.name}</Typography>
                  <Typography variant="caption" sx={{ color: ROLE_COLORS[selectedUser.role], fontWeight: 500 }}>
                    {ROLE_LABELS[selectedUser.role] || selectedUser.role}
                  </Typography>
                </Box>
              </Box>
              <IconButton size="small" onClick={() => setSelectedUser(null)}><Close /></IconButton>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* User Info */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2.5 }}>
              {selectedUser.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">{selectedUser.email}</Typography>
                </Box>
              )}
              {selectedUser.company && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Business sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">{selectedUser.company.name}</Typography>
                </Box>
              )}
              {selectedUser.department && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Work sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">{selectedUser.department}</Typography>
                </Box>
              )}
              {selectedUser.manager && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">Manager: {selectedUser.manager.name}</Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Overall Progress */}
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Overall Progress</Typography>
            {selectedUser.totalTasks === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No KPIs assigned yet</Typography>
            ) : (
              <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={selectedUser.completionPercentage}
                    sx={{
                      flex: 1, height: 10, borderRadius: 5,
                      bgcolor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': { bgcolor: getProgressColor(selectedUser.completionPercentage) },
                    }}
                  />
                  <Typography variant="body1" fontWeight="bold">{fmtPct(selectedUser.completionPercentage)}</Typography>
                </Box>
                {(() => {
                  const cfg = STATUS_CONFIG[getUserStatus(selectedUser)] || STATUS_CONFIG.no_kpi;
                  return (
                    <Chip
                      size="small"
                      label={cfg.label}
                      sx={{ bgcolor: cfg.bgcolor, color: cfg.textColor, fontWeight: 600, fontSize: 11 }}
                    />
                  );
                })()}
              </Box>
            )}

            {/* Task Status Breakdown */}
            {selectedUser.totalTasks > 0 && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Task Breakdown  <Typography component="span" variant="caption" color="text.secondary">({selectedUser.totalTasks} total)</Typography>
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {[
                    { label: 'Achieved',     count: selectedUser.achieved    || 0, icon: <CheckCircle sx={{ fontSize: 16, color: TASK_STATUS_COLORS.achieved }} />,    color: TASK_STATUS_COLORS.achieved },
                    { label: 'In Progress',  count: selectedUser.inProgress  || 0, icon: <RadioButtonUnchecked sx={{ fontSize: 16, color: TASK_STATUS_COLORS.inProgress }} />, color: TASK_STATUS_COLORS.inProgress },
                    { label: 'Not Achieved', count: selectedUser.notAchieved || 0, icon: <Cancel sx={{ fontSize: 16, color: TASK_STATUS_COLORS.notAchieved }} />,        color: TASK_STATUS_COLORS.notAchieved },
                    { label: 'Not Started',  count: selectedUser.notStarted  || 0, icon: <RemoveCircle sx={{ fontSize: 16, color: TASK_STATUS_COLORS.notStarted }} />,   color: TASK_STATUS_COLORS.notStarted },
                  ].map(row => (
                    <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {row.icon}
                      <Typography variant="body2" sx={{ flex: 1 }}>{row.label}</Typography>
                      <Typography variant="body2" fontWeight="bold">{row.count}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32, textAlign: 'right' }}>
                        {fmtPct((row.count / selectedUser.totalTasks) * 100)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {/* Last Activity */}
            {selectedUser.totalTasks > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Last Activity</Typography>
                <Typography variant="body2" color="text.secondary">
                  {new Date(selectedUser.lastUpdate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                </Typography>
              </>
            )}

            {/* Per-KPI Breakdown */}
            {selectedUser.kpisBreakdown && selectedUser.kpisBreakdown.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  KPI Breakdown
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    ({selectedUser.kpisBreakdown.length} KPI{selectedUser.kpisBreakdown.length !== 1 ? 's' : ''})
                  </Typography>
                </Typography>
                {selectedUser.kpisBreakdown.map((kpi, i) => {
                  const kpiCfg = STATUS_CONFIG[kpi.status] || STATUS_CONFIG.no_kpi;
                  return (
                    <Box key={String(kpi.kpiId)} sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                        <Typography variant="body2" fontWeight="medium">KPI {i + 1}</Typography>
                        <Chip
                          size="small"
                          label={kpiCfg.label}
                          sx={{ bgcolor: kpiCfg.bgcolor, color: kpiCfg.textColor, fontSize: 10, fontWeight: 600, height: 20 }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <LinearProgress
                          variant="determinate"
                          value={kpi.completionPct}
                          sx={{
                            flex: 1, height: 4, borderRadius: 2, bgcolor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': { bgcolor: kpiCfg.color },
                          }}
                        />
                        <Typography variant="caption" fontWeight="medium">{kpi.completionPct}%</Typography>
                      </Box>
                      {kpi.reviewDate && (
                        <Typography variant="caption" color="text.secondary">
                          {kpi.daysOverdue
                            ? `${kpi.daysOverdue}d overdue`
                            : kpi.daysRemaining != null
                              ? `${kpi.daysRemaining}d remaining`
                              : 'Due today'}
                          {' · '}{new Date(kpi.reviewDate).toLocaleDateString()}
                        </Typography>
                      )}
                      {kpi.areas.length > 0 && (
                        <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                          {kpi.areas.map(area => {
                            const areaCfg = STATUS_CONFIG[area.status] || STATUS_CONFIG.no_kpi;
                            return (
                              <Box key={String(area.areaId)} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25 }}>
                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: areaCfg.color, flexShrink: 0 }} />
                                <Typography variant="caption" noWrap sx={{ flex: 1 }}>{area.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{area.completionPct}%</Typography>
                                {area.daysOverdue && (
                                  <Typography variant="caption" color="error.main" sx={{ fontSize: 10 }}>
                                    {area.daysOverdue}d over
                                  </Typography>
                                )}
                                {!area.daysOverdue && area.daysRemaining != null && (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                                    {area.daysRemaining}d
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </>
            )}

          </Box>
        )}
      </Drawer>

    </Box>
  );
};

export default Dashboard;
